import React, { useState, useRef } from "react";
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  editor: {
    padding: theme.spacing(2),
    minHeight: 150,
    maxHeight: 400,
    overflowY: "auto",
    outline: "none",
    "&:focus": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  counter: {
    padding: theme.spacing(1),
    textAlign: "right",
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  counterWarning: {
    color: theme.palette.warning.main,
  },
  counterError: {
    color: theme.palette.error.main,
  },
}));

const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Escribe aquí...",
  maxLength,
}) => {
  const classes = useStyles();
  const editorRef = useRef(null);
  const [charCount, setCharCount] = useState(value.length);

  const handleInput = (e) => {
    const text = e.target.innerText;
    setCharCount(text.length);

    if (maxLength && text.length > maxLength) {
      return;
    }

    if (onChange) {
      onChange(text);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = prompt("Ingresa la URL:");
    if (url) {
      execCommand("createLink", url);
    }
  };

  const getCounterClass = () => {
    if (!maxLength) return classes.counter;
    const percentage = (charCount / maxLength) * 100;
    if (percentage >= 100) return `${classes.counter} ${classes.counterError}`;
    if (percentage >= 90) return `${classes.counter} ${classes.counterWarning}`;
    return classes.counter;
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.toolbar}>
        <Tooltip title="Negrita">
          <IconButton size="small" onClick={() => execCommand("bold")}>
            <FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cursiva">
          <IconButton size="small" onClick={() => execCommand("italic")}>
            <FormatItalic />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Lista con viñetas">
          <IconButton
            size="small"
            onClick={() => execCommand("insertUnorderedList")}
          >
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <IconButton
            size="small"
            onClick={() => execCommand("insertOrderedList")}
          >
            <FormatListNumbered />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Insertar enlace">
          <IconButton size="small" onClick={insertLink}>
            <LinkIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        ref={editorRef}
        className={classes.editor}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        dangerouslySetInnerHTML={{ __html: value }}
        data-placeholder={placeholder}
      />
      {maxLength && (
        <Typography className={getCounterClass()}>
          {charCount} / {maxLength}
        </Typography>
      )}
    </Paper>
  );
};

export default RichTextEditor;
